import { memo, useId, useState } from "react";
import { useReducedMotion } from "motion/react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import { cn } from "../../ui/utils";
import {
  CHART_HEIGHT,
  DOUGHNUT_PLOT_PX,
  RADIAL_ANIM_EASING,
  RADIAL_ANIM_MS,
  RADIAL_END_ANGLE,
  RADIAL_START_ANGLE,
  shade,
} from "./chartTheme";
// The same active shape and hide/show behaviour the dashboard card renders, not a copy of them —
// see components/charts/donutCore.
import { applyHidden, renderActiveShape, useSliceToggle } from "../../charts/donutCore";
// The doughnut is not drawn here at all — it is the platform's own donut, the same component the
// Risk Quadrant's "Findings by Severity" panel renders. See ChatDoughnut below.
import { DonutPieChart } from "../../ChartsComponent/DonutPieChart/DonutPieChart";
import { RadialLegend, RadialTooltip, type RadialLegendItem } from "./ChartChrome";
import { buildSrSummary, type NormalizedChart } from "./resolveChart";

interface PieLabelProps {
  cx: number;
  cy: number;
  midAngle: number;
  innerRadius: number;
  outerRadius: number;
  percent: number;
}

function renderPercentLabel(p: PieLabelProps) {
  const { cx, cy, midAngle, innerRadius, outerRadius, percent } = p;
  if (!percent || percent < 0.08) return null;
  const RAD = Math.PI / 180;
  const r = innerRadius + (outerRadius - innerRadius) * 0.62;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);
  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      fontSize={11}
      fontWeight={700}
      textAnchor="middle"
      dominantBaseline="central"
      style={{ textShadow: "0 1px 2px rgba(0,0,0,0.45)" }}
    >
      {Math.round(percent * 100)}%
    </text>
  );
}

/**
 * The chat doughnut IS the platform's donut — `ChartsComponent/DonutPieChart` — configured, not
 * reimplemented.
 *
 * <h2>Why it delegates instead of drawing its own</h2>
 * It used to draw its own, sharing only the leader-label renderer out of {@code charts/donutCore},
 * and the two drifted where they did not share: the chat ring swept from 12 o'clock clockwise while
 * the platform donut leaves recharts' 3 o'clock default alone. That is not cosmetic. Leader labels
 * run sideways, so how far one can get clear of the arc is governed by cos(midAngle) — starting at
 * 12 o'clock puts the first slices at the top of the circle, where cos ≈ 0, every label resolves to
 * near the same x, and a run of slivers ends up stacked over the ring with the leader lines crossing
 * through the text. Every distribution charted here is ordered smallest-band-first, so the slivers
 * were exactly the slices that broke. Delegating fixes that by construction and means the chat and
 * the dashboard cannot disagree about a chart again.
 *
 * <h2>The two props that protect grounding</h2>
 * {@code normalizeChartData} would re-sort by value and fold a long tail into a synthetic "Other"
 * bucket. Both are wrong here: the rows are a platform DTO the answer beside them is quoting, and an
 * "Other" category is one the backend never produced. {@code sort="none"} and a cap equal to the row
 * count switch them off, so every value is rendered exactly as the mapper emitted it.
 *
 * <p>No title or subtitle is passed — {@code ChartMessage}'s card already renders them.
 */
function ChatDoughnut({ chart }: { chart: NormalizedChart }) {
  return (
    <DonutPieChart
      variant="donut"
      data={chart.rows}
      nameKey={chart.categoryKey}
      valueKey={chart.series[0].key}
      // normalizeChart resolves each row's severity colour onto `fill`.
      colorKey="fill"
      height={DOUGHNUT_PLOT_PX}
      sort="none"
      maxSlices={chart.rows.length}
      showSliceLabels
      labelStyle="leader"
      legendPosition="right"
      // No share column in the chat legend. The column the transcript gives it is narrow, so a
      // second number per row wraps and the rows stop lining up — and the shares are already drawn
      // on the ring beside their slices.
      showLegendShare={false}
      showDataTable={false}
      showWarnings={false}
      unit={chart.unit}
      centerLabel="Total"
      // The donut labels itself as the image, so ChartMessage's wrapper stands down rather than
      // nesting a second role="img" around it. Same summary either way — the chat's own, which
      // reads values in the wording the rest of the transcript uses.
      ariaLabel={buildSrSummary(chart)}
    />
  );
}

/** The solid pie: its own small chart, with the percentages written inside the slices. */
function ChatPie({ chart }: { chart: NormalizedChart }) {
  const reduce = useReducedMotion();
  const [active, setActive] = useState<number | null>(null);
  const uid = useId().replace(/:/g, "");

  const valueKey = chart.series[0].key;
  const size = CHART_HEIGHT.radial;

  // Legend rows keep their real value and share even while hidden — the dashboard card does the same,
  // so a toggled-off row still tells you what you switched off.
  const items: RadialLegendItem[] = chart.rows.map((r) => {
    const value = Number(r[valueKey]) || 0;
    return {
      label: String(r[chart.categoryKey] ?? ""),
      value,
      color: r.fill ?? "#6366f1",
      share: chart.total > 0 ? Math.round((value / chart.total) * 100) : 0,
    };
  });

  const { hidden, toggle } = useSliceToggle(chart.rows.length);
  // Feed the Pie the zeroed rows, not chart.rows: that value change is what makes recharts tween the
  // sector shut instead of the pie snapping to a new shape.
  const rows = applyHidden(chart.rows, hidden, (r) => String(r[chart.categoryKey] ?? ""), valueKey);

  return (
    <div
      className={cn(
        "flex items-center gap-3",
        chart.legend === "right" ? "flex-col @[440px]:flex-row" : "flex-col",
      )}
    >
      <div
        className="relative shrink-0"
        // Native mouseleave is the reliable "cursor left" signal; Recharts skips per-slice onMouseLeave (hover sticks).
        onMouseLeave={() => setActive(null)}
        style={{
          width: size,
          height: size,
          filter: "drop-shadow(0 6px 12px rgba(15, 23, 42, 0.14))",
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart onMouseLeave={() => setActive(null)}>
            <defs>
              {chart.rows.map((r, i) => {
                const c = r.fill ?? "#6366f1";
                return (
                  <linearGradient key={i} id={`${uid}-slice-${i}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={shade(c, 0.24)} />
                    <stop offset="100%" stopColor={c} />
                  </linearGradient>
                );
              })}
            </defs>
            {/* Snap between slices instead of gliding, and draw no cursor — matches DonutPanel. */}
            <Tooltip
              content={<RadialTooltip unit={chart.unit} total={chart.total} />}
              isAnimationActive={false}
              cursor={false}
            />
            <Pie
              data={rows}
              dataKey={valueKey}
              nameKey={chart.categoryKey}
              cx="50%"
              cy="50%"
              innerRadius={0}
              outerRadius="86%"
              startAngle={RADIAL_START_ANGLE}
              endAngle={RADIAL_END_ANGLE}
              paddingAngle={0}
              cornerRadius={4}
              stroke="none"
              // Inside the slices, where a solid pie has room for them.
              label={renderPercentLabel}
              labelLine={false}
              // -1, not undefined: undefined lets recharts fall back to its own tooltip-driven active
              // state, which leaves the glow stuck on a slice after the cursor has left.
              activeIndex={active ?? -1}
              activeShape={renderActiveShape}
              onMouseEnter={(_, i: number) => setActive(i)}
              onMouseLeave={() => setActive(null)}
              isAnimationActive={!reduce}
              animationDuration={RADIAL_ANIM_MS}
              animationEasing={RADIAL_ANIM_EASING}
            >
              {/* No opacity dimming of the unhovered slices: the card pops the hovered one and leaves
                  the rest alone, and fading them here read as a different chart on hover. */}
              {rows.map((r, i) => (
                <Cell key={i} fill={`url(#${uid}-slice-${i})`} stroke="none" />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>

      {chart.legend !== "none" && (
        <RadialLegend items={items} unit={chart.unit} active={active} hidden={hidden} onToggle={toggle} />
      )}
    </div>
  );
}

export const RadialChart = memo(function RadialChart({ chart }: { chart: NormalizedChart }) {
  return chart.kind === "doughnut" ? <ChatDoughnut chart={chart} /> : <ChatPie chart={chart} />;
});
